import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MEMBERSHIP_USER_ACCESS_LEVEL, MembershipCloudUser } from 'src/features/membership/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { useUpdateMembershipCloudUser } from 'src/features/MembershipCloudUsers/api/useUpdateMembershipCloudUser';
import { LIST_MEMBERSHIP_CLOUD_USERS_RESOURCE } from 'src/features/MembershipCloudUsers/api/useListMembershipCloudUsers';
import { membershipUsersMessages } from 'src/features/MembershipCloudUsers/utils/messages';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { Button } from 'src/registry/ui/Button';
import { cn } from 'src/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { useOnError } from 'src/utils/errors';
import { useForm } from 'react-hook-form';
import {
    membershipCloudUserEditFormSchema,
    MembershipCloudUserEditFormSchemaValues,
} from 'src/features/MembershipCloudUsers/utils/formSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from 'src/registry/ui/Form';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { MEMBERSHIP_USER_ACCESS_LEVEL_TRANSLATIONS } from 'src/features/MembershipCloudUsers/utils/constants';

interface MembershipCloudUserFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCloudUser: MembershipCloudUser;
    membershipId: string;
    children?: React.ReactNode;
}

function UpdateMembershipCloudUserFormModal<TData>({
    className,
    semantics,
    children,
    selectedCloudUser,
    membershipId,
}: MembershipCloudUserFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateMembershipCloudUser({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_CLOUD_USERS_RESOURCE] });
            setIsModalOpen(false);
            form.reset();

            toast({
                title: formatMessage(membershipUsersMessages.updateMembershipCloudUserSuccess),
                description: formatMessage(membershipUsersMessages.updateMembershipCloudUserSuccessDescription, {
                    entry: selectedCloudUser.cloud_user.name,
                }),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const onError = useOnError();

    const form = useForm<MembershipCloudUserEditFormSchemaValues>({
        resolver: zodResolver(membershipCloudUserEditFormSchema),
        defaultValues: {
            access_level: selectedCloudUser?.access_level ?? undefined,
        },
    });

    React.useEffect(() => {
        form.reset();
    }, [form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: MembershipCloudUserEditFormSchemaValues) =>
            mutate({
                ...values,
                parkingId,
                membershipId,
                userId: selectedCloudUser.id,
            }),
        [mutate, parkingId, selectedCloudUser.id, membershipId],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                        {formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>
                                {formatMessage(generalMessages.editEntry, {
                                    entry: semantics?.entryName?.toLowerCase(),
                                })}
                            </DialogTitle>
                            <DialogDescription>
                                {formatMessage(membershipUsersMessages.editMembershipCloudUserSubtitle, {
                                    entry: selectedCloudUser.cloud_user.name,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
                        <SelectFormField<MembershipCloudUserEditFormSchemaValues>
                            form={form}
                            required
                            name="access_level"
                            label={formatMessage(membershipUsersMessages.accessLevel)}
                            options={Object.values(MEMBERSHIP_USER_ACCESS_LEVEL)}
                            optionTranslationMap={MEMBERSHIP_USER_ACCESS_LEVEL_TRANSLATIONS}
                        />
                    </Form>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedCloudUser && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(generalMessages.editEntry, {
                            entry: semantics?.entryName?.toLowerCase(),
                        })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default UpdateMembershipCloudUserFormModal;
